import { useCallback, useRef, useState } from 'react';

export interface HistorySnapshot<T> {
  past: T[];
  present: T;
  future: T[];
}

export interface HistoryControls<T> {
  state: T;
  /**
   * `coalesce` folds rapid consecutive edits into one undo step — typing a
   * customer name should be one undo, not thirty.
   */
  set: (updater: T | ((previous: T) => T), options?: { coalesce?: boolean }) => void;
  /** Replaces the value and clears history (new quote, loaded draft). */
  reset: (next: T) => void;
  undo: () => boolean;
  redo: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
}

interface Options {
  /** How many undo steps to retain. */
  limit?: number;
  /** Window within which consecutive coalescing edits merge. */
  coalesceMs?: number;
}

/**
 * Undo/redo for a single piece of state.
 *
 * Deliberately generic and storage-agnostic: the quote builder uses it today,
 * anything else can use it tomorrow without touching this file.
 */
export function useHistoryState<T>(initial: T, options: Options = {}): HistoryControls<T> {
  const { limit = 60, coalesceMs = 550 } = options;
  const [snapshot, setSnapshot] = useState<HistorySnapshot<T>>({
    past: [],
    present: initial,
    future: [],
  });
  const lastCommitAt = useRef(0);

  const set = useCallback<HistoryControls<T>['set']>(
    (updater, { coalesce = false } = {}) => {
      const now = Date.now();
      const shouldMerge = coalesce && now - lastCommitAt.current < coalesceMs;
      lastCommitAt.current = now;

      setSnapshot((current) => {
        const next =
          typeof updater === 'function' ? (updater as (previous: T) => T)(current.present) : updater;
        if (Object.is(next, current.present)) return current;

        return {
          past: shouldMerge ? current.past : [...current.past, current.present].slice(-limit),
          present: next,
          future: [],
        };
      });
    },
    [limit, coalesceMs],
  );

  const reset = useCallback((next: T) => {
    lastCommitAt.current = 0;
    setSnapshot({ past: [], present: next, future: [] });
  }, []);

  const undo = useCallback(() => {
    let moved = false;
    setSnapshot((current) => {
      const previous = current.past[current.past.length - 1];
      if (previous === undefined) return current;
      moved = true;
      return {
        past: current.past.slice(0, -1),
        present: previous,
        future: [current.present, ...current.future],
      };
    });
    lastCommitAt.current = 0;
    return moved;
  }, []);

  const redo = useCallback(() => {
    let moved = false;
    setSnapshot((current) => {
      const next = current.future[0];
      if (next === undefined) return current;
      moved = true;
      return {
        past: [...current.past, current.present].slice(-limit),
        present: next,
        future: current.future.slice(1),
      };
    });
    lastCommitAt.current = 0;
    return moved;
  }, [limit]);

  return {
    state: snapshot.present,
    set,
    reset,
    undo,
    redo,
    canUndo: snapshot.past.length > 0,
    canRedo: snapshot.future.length > 0,
  };
}
